import { getCameraViewMatrix } from "@/content/core/content.common";
import { ErrorWebXR } from "@/core/errors/error.webxr";
import Utils from "@/core/utils/utils";
import { Angle, Matrix, Quaternion, Scene, Vector3, WebXRDefaultExperience, WebXRDomOverlay, WebXRRenderTarget, WebXRState } from "babylonjs";

//------------------------------------------------------------------------------ Base
class XRModuleBase {
  protected xr?: WebXRDefaultExperience;
  constructor(xr?: WebXRDefaultExperience) {
    this.xr = xr;
  }
}

//------------------------------------------------------------------------------ Session
export class XRModuleSessionOptions {
  public domOverlayElement?: Element;
  public domOverlayElementName?: string;
  public disableDefaultUI: boolean = false;
  public enableHitTest: boolean = true;
  public enableCameraAccess: boolean = true;
  public onEnteringXR?: () => void;
  public onExitingXR?: () => void;
  public onInXR?: () => void;
  public onNotInXR?: () => void;
  public onError?: ( error:Error ) => void;

  public get enableDomOverlay():boolean {
    const enableDomOverlay = (this.domOverlayElement !== undefined || this.domOverlayElementName !== undefined);
    return enableDomOverlay;
  }

  public get requiredFeatures(): string[] {
    const requiredFeatures: string[] = [];
    if( this.enableDomOverlay ) requiredFeatures.push('dom-overlay');
    return requiredFeatures;
  }

  public get optionalFeatures(): string[] {
    const optionalFeatures: string[] = [];
    if( this.enableHitTest ) optionalFeatures.push('hit-test');
    if( this.enableCameraAccess ) optionalFeatures.push('camera-access');
    return optionalFeatures;
  }
}

export class XRModuleSession extends XRModuleBase {
  
  private options:XRModuleSessionOptions;
  
  constructor() {
    super();
    this.options = new XRModuleSessionOptions();
  }

  public dispose() {
    this.clearXRStateChangedObservable();
  }

  public async init( scene: Scene, options?:XRModuleSessionOptions ): Promise<WebXRDefaultExperience|undefined> {
    if( this.xr ) {
      return this.xr;
    }
    if( options ) {
      this.options = options;
    }
    if( this.options.disableDefaultUI ) {
      await this.initWithCustomUI( scene );
    } else {
      await this.initWithDefaultUI( scene );
    }
    this.addXRStateChangedObservable();
    return this.xr;
  }

  private async initWithDefaultUI( scene: Scene ) {
    try {
      const requiredFeatures = this.options.requiredFeatures;
      const optionalFeatures = this.options.optionalFeatures;
      this.xr = await scene.createDefaultXRExperienceAsync({
        uiOptions: {
          sessionMode: "immersive-ar",
          referenceSpaceType: "local",
          onError: this.options.onError,
          requiredFeatures: requiredFeatures,
          optionalFeatures: optionalFeatures
        },
        optionalFeatures: (optionalFeatures.length > 0),
      });
      if( this.options.enableDomOverlay ) {
        if( this.xr.baseExperience ) {
          const { featuresManager } = this.xr.baseExperience;
          featuresManager.enableFeature(WebXRDomOverlay, 'latest', {
            element: this.options.domOverlayElementName,
          });
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  private async initWithCustomUI( scene: Scene ) {
    this.xr = await scene.createDefaultXRExperienceAsync({
      disableDefaultUI: this.options.disableDefaultUI,
    });
    const isSupported = await this.xr.baseExperience.sessionManager.isSessionSupportedAsync('immersive-ar');
    if( !isSupported ) {
      const error = new Error('app.immersal.webxr.startXR: immersive-ar is not supported.');
      error.name = ErrorWebXR.NOT_SUPPPORTED;
      throw error;
    }
  }

  public async startXR() {
    if(!this.xr) {
      return;
    }
    const sessionMode: XRSessionMode = "immersive-ar";
    const referenceSpaceLocal: XRReferenceSpaceType = "local";
    const renderTarget: WebXRRenderTarget = this.xr.renderTarget;
    let domOverlay: XRDOMOverlayInit | undefined = undefined;
    if( this.options.domOverlayElement ) {
      domOverlay = { root: this.options.domOverlayElement };
    }
    const sessionCreationOptions: XRSessionInit = {
      requiredFeatures: this.options.requiredFeatures,
      optionalFeatures: this.options.optionalFeatures,
      domOverlay: domOverlay,
    }
    try {
      await this.xr.baseExperience.enterXRAsync(sessionMode, referenceSpaceLocal, renderTarget, sessionCreationOptions);
    } catch( e ) {
      let permissionDenied = false;
      if( e instanceof DOMException ) {
        const domE = e as DOMException;
        permissionDenied = ( domE.name === 'NotSupportedError' );
        // NOTE: NotSupportedError could also mean a range of different errors, but here its assumed it is permission denied.
      }
      if( permissionDenied ) {
        const error = new Error('app.immersal.webxr.startXR: permission denied.');
        error.name = ErrorWebXR.PERMISSION_DENIED;
        throw error;
      } else {
        const error = new Error('app.immersal.webxr.startXR: undefined.');
        error.name = ErrorWebXR.UNDEFINED;
        throw error;
      }
    }
  }

  public async stopXR() {
    if( !this.xr ) {
      return;
    }
    await this.xr.baseExperience.exitXRAsync();
  }

  public get isXRRunning(): boolean {
    // The !! converts the value to a boolean.
    // If the value is truthy (e.g., true, a non-zero number, or a non-empty string), it evaluates to true.
    // If the value is falsy (e.g., false, null, undefined, 0, or an empty string), it evaluates to false.
    const xrRunning = !!(this.xr?.baseExperience?.sessionManager?.inXRSession);
    return xrRunning;
  }

  private addXRStateChangedObservable() {
    if(!this.xr) {
      return;
    }
    this.xr.baseExperience.onStateChangedObservable.add((state) => {
      if (state === WebXRState.ENTERING_XR) {
        if( this.options.onEnteringXR ) {
          this.options.onEnteringXR();
        }
      } else if (state === WebXRState.EXITING_XR) {
        if( this.options.onExitingXR ) {
          this.options.onExitingXR();
        }
      } else if (state === WebXRState.IN_XR) {
        if( this.options.onInXR ) {
          this.options.onInXR();
        }        
      } else if (state === WebXRState.NOT_IN_XR) {
        if( this.options.onNotInXR ) {
          this.options.onNotInXR();
        }
      }
    });
  }

  private clearXRStateChangedObservable() {
    if(!this.xr) {
      return;
    }
    this.xr.baseExperience.onStateChangedObservable.clear();
  }
}

//------------------------------------------------------------------------------ Hit Test
interface XRHitTest {
  uuid: string;
  source: XRHitTestSource;
  matrix: Matrix;
  position: Vector3;
  rotation: Quaternion;
  scale: Vector3;
  timestamp: number;
}

export class XRModuleHitTest extends XRModuleBase {
  
  protected hitTests: XRHitTest[] = [];
  
  constructor(xr: WebXRDefaultExperience) {
    super(xr);
  }

  // rayOrigin uses (NDC) Normalized Device Coordinates
  // rayOrigin coordinate range is x:[-1,1], y:[-1,1], z:[0]
  // rayOrigin is at center of screen by default
  // rayDirection points forward in view space
  public async initHitTest(
    rayOrigin: DOMPointInit = { x:0, y:0, z:0 }, 
    rayDirection: DOMPointInit = { x:0, y:0, z:-1 }
  ): Promise<string | undefined> {
    try {
      if (!this.xr) {
        throw new Error('app.webxr.hitTestUpdate: no xr.');
      }
      const xrSessionManager = this.xr.baseExperience.sessionManager;
      if( !xrSessionManager.inXRSession ) {
        throw new Error('app.webxr.hitTestUpdate: not in XR session.');
      }
      const session = xrSessionManager.session;
      if( !session ) {
        throw new Error('app.webxr.hitTestUpdate: no session.');
      }
      const hitTestSupported = session.enabledFeatures.includes("hit-test");
      if( !hitTestSupported ) {
        throw new Error('app.webxr.hitTestUpdate: hit test not supported.');
      }
      if( !session.requestHitTestSource ) {
        throw new Error('app.webxr.hitTestUpdate: no requestHitTestSource.');
      }
      const referenceSpaceViewer = await session.requestReferenceSpace("viewer");
      const hitTestSourceOptions: XRHitTestOptionsInit = {
        space: referenceSpaceViewer,
        offsetRay: new XRRay(rayOrigin, rayDirection),
      };
      const source = await session.requestHitTestSource!( hitTestSourceOptions );
      const hitTest:XRHitTest = {
        uuid: Utils.UUID(),
        source: source,
        matrix: new Matrix(),
        position: new Vector3(0, 0, 0),
        rotation: new Quaternion(0, 0, 0, 1),
        scale: new Vector3(1, 1, 1),
        timestamp: 0,
      }
      this.hitTests.push( hitTest );
      return hitTest.uuid;
    } catch (error) {
      console.error(error);
    }
  }

  public getHitTest(uuid: string): XRHitTest | undefined {
    const hitTest = this.hitTests.find( (hitTest) => hitTest.uuid === uuid );
    if( hitTest === undefined ) return;
    return hitTest;
  }

  public killHitTest(uuid: string): void {
    const hitTestIndex = this.hitTests.findIndex( (hitTest) => hitTest.uuid === uuid );
    if( hitTestIndex === -1 ) {
      return;
    }
    const hitTest = this.hitTests[hitTestIndex];
    hitTest.source.cancel();
    this.hitTests.splice(hitTestIndex, 1);
  }

  public killAllHitTests(): void {
    for( const hitTest of this.hitTests ) {
      hitTest.source.cancel();
    }
    this.hitTests = [];
  }

  public updateHitTests() {
    if( this.hitTests.length === 0 ) {
      return;
    }
    try {
      if (!this.xr) {
        throw new Error('app.webxr.hitTestUpdate: no xr.');
      }
      const xrSessionManager = this.xr.baseExperience.sessionManager;
      if( !xrSessionManager.inXRSession ) {
        throw new Error('app.webxr.hitTestUpdate: not in XR session.');
      }
      const session = xrSessionManager.session;
      if( !session ) {
        throw new Error('app.webxr.hitTestUpdate: no session.');
      }
      const frame = xrSessionManager.currentFrame;
      if( !frame ) {
        throw new Error('app.webxr.hitTestUpdate: no frame.');
      }
      for( const hitTest of this.hitTests ) {
        const hitTestResults: XRHitTestResult[] = frame.getHitTestResults(hitTest.source);
        for( const hitTestResult of hitTestResults ) {
          const pose: XRPose | undefined = hitTestResult.getPose(xrSessionManager.referenceSpace);
          if( !pose ) {
            continue;
          }
          const hitMatrix = pose.transform.matrix;
          hitTest.matrix.fromArray( hitMatrix );
          hitTest.matrix.decompose(hitTest.scale, hitTest.rotation, hitTest.position);
          hitTest.timestamp = performance.now();
        }
      }
    } catch (error) {
      console.error(error);
    }
  }
}

//------------------------------------------------------------------------------ Floor Estimate
export class XRModuleFloorEstimateOptions {
  public initialFloorEstimate: number = 0;
  public floorEstimateSmoothingRate: number = 0.2;
}

export class XRModuleFloorEstimate extends XRModuleHitTest {
  
  private options: XRModuleFloorEstimateOptions;
  private hitTestUUID?: string;
  private floorEstimate: number = 0;
  private floorEstimateSmoothed: number = 0;
  private buffer: number[] = [];
  private bufferSize: number = 10;
  private sampleTime: number = 0;
  private sampleRateMs: number = 1000;
  private scene: Scene;

  constructor(xr: WebXRDefaultExperience, scene: Scene) {
    super(xr);
    this.scene = scene;
    this.options = new XRModuleFloorEstimateOptions();
    this.floorEstimate = this.options.initialFloorEstimate;
    this.floorEstimateSmoothed = this.options.initialFloorEstimate;
  }

  public async initFloorEstimate( options?:XRModuleFloorEstimateOptions ): Promise<void> {
    if( options ) {
      this.options = options;
      this.floorEstimate = this.options.initialFloorEstimate;
      this.floorEstimateSmoothed = this.options.initialFloorEstimate;
    }
    const rayOrigin: DOMPointInit = { x:0, y:0, z:0 };
    const rayDirection: DOMPointInit = {
      x: 0,  // No horizontal offset
      y: -0.5, // Slightly downward
      z: -1, // Forward direction in viewer space
    };
    const magnitude = Math.sqrt(rayDirection.x! ** 2 + rayDirection.y! ** 2 + rayDirection.z! ** 2);
    rayDirection.x! /= magnitude;
    rayDirection.y! /= magnitude;
    rayDirection.z! /= magnitude;
    this.hitTestUUID = await this.initHitTest(rayOrigin, rayDirection);
  }

  public updateFloorEstimate() {
    this.updateHitTests();

    const hitTest = this.getHitTest();

    if (this.scene.activeCamera === undefined) {
      return;
    }

    const cameraY = this.scene.activeCamera?.position.y ?? 0.0;
    
    if( hitTest && hitTest.position.y < cameraY ) {
      const isNewSample = (hitTest.timestamp - this.sampleTime) > this.sampleRateMs;
      if( isNewSample ) {
        this.sampleTime = hitTest.timestamp;
        this.floorEstimate = this.weightedMovingAverage( hitTest.position.y );
      }
    }
    
    this.floorEstimateSmoothed += ( this.floorEstimate - this.floorEstimateSmoothed ) * this.options.floorEstimateSmoothingRate;
  }

  /**
   * Updates the floor estimate using a weighted moving average
   * where weights are based on the difference from the average.
   */  
  private weightedMovingAverage( value: number ): number {
    this.buffer.push( value );
    if( this.buffer.length > this.bufferSize ) {
      this.buffer.shift();
    }
    // Compute the average of the buffer
    let sum = 0;
    for (const value of this.buffer) {
      sum += value;
    }
    const average = sum / this.buffer.length;
    // Compute weights based on the difference from the average
    const weights: number[] = [];
    let weightSum = 0;
    for (const value of this.buffer) {
      const difference = Math.abs(value - average);
      const weight = 1 / (1 + difference); // Smaller difference = larger weight
      weights.push(weight);
      weightSum += weight;
    }
    // Calculate the weighted sum
    let weightedSum = 0;
    for (let i = 0; i < this.buffer.length; i++) {
      weightedSum += this.buffer[i] * weights[i];
    }
    const result = weightedSum / weightSum;
    return result;
  }

  public override getHitTest(): XRHitTest | undefined {
    if( this.hitTestUUID === undefined ) return;
    return super.getHitTest( this.hitTestUUID );
  }

  public getHitTestMatrix(): Matrix | undefined {
    const hitTest = this.getHitTest();
    const matrix = hitTest?.matrix.clone();
    return matrix;
  }

  public getHitTestPosition(): Vector3 | undefined {
    const hitTest = this.getHitTest();
    const position = hitTest?.position.clone();
    return position;
  }

  public getHitTestRotation(): Quaternion | undefined {
    const hitTest = this.getHitTest();
    const rotation = hitTest?.rotation.clone();
    return rotation;
  }

  public getHitTestScale(): Vector3 | undefined {
    const hitTest = this.getHitTest();
    const scale = hitTest?.scale.clone();
    return scale;
  }

  public getFloorEstimate(): number {
    return this.floorEstimate;
  }

  public getFloorEstimateSmoothed(): number {
    return this.floorEstimateSmoothed;
  }
}